import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  ClipboardList,
  Factory,
  PackageOpen,
  Plus,
  Settings2,
  ShoppingCart,
  Target,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { PlanTab } from '@/components/manufacturing/planning/PlanTab';
import { PartsTab } from '@/components/manufacturing/planning/PartsTab';
import { CalendarTab } from '@/components/manufacturing/planning/CalendarTab';
import { CashTab } from '@/components/manufacturing/planning/CashTab';
import { MissingInfoTab } from '@/components/manufacturing/planning/MissingInfoTab';
import { PlanLineDialog } from '@/components/manufacturing/planning/PlanLineDialog';
import { PlanningSettingsDialog } from '@/components/manufacturing/planning/PlanningSettingsDialog';
import { CreatePurchaseOrdersDialog } from '@/components/manufacturing/planning/CreatePurchaseOrdersDialog';
import { shortDate } from '@/components/manufacturing/planning/shared';
import { usePlanningModel, type PlanLineRow } from '@/features/manufacturing/planning/hooks';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import { useAuth } from '@/app/AuthProvider';
import { formatCurrency } from '@/lib/formatters';

type TabId = 'plan' | 'parts' | 'calendar' | 'cash' | 'missing';

const TAB_IDS: TabId[] = ['plan', 'parts', 'calendar', 'cash', 'missing'];

/**
 * Production planning + procurement calendar.
 *
 * Targets ("50 Renew Electric by Dec 15", "6 Renew Manual every day",
 * "120 in November") are exploded through the BOMs, netted against stock
 * and open POs, and turned into one answer per purchased part: when it
 * runs out, the last day to order, the ideal day to order, and what to
 * buy once MOQs, pack sizes and price breaks are applied. Lead times come
 * from what vendors actually did (PO ordered → goods receipt), falling
 * back to the quoted figure. The Missing info tab lists what the planner
 * still needs from us to answer with confidence.
 */
export function ProductionPlanningPage() {
  const { role } = useAuth();
  const [params, setParams] = useSearchParams();
  const { model, isLoading, error, settings, settingsSaved, planLines, holidays } =
    usePlanningModel();

  const canEdit = role === 'super_admin' || role === 'admin' || role === 'manager';
  const canCreatePos = role === 'super_admin' || role === 'admin';

  const tabParam = params.get('tab');
  const tab: TabId = TAB_IDS.includes(tabParam as TabId) ? (tabParam as TabId) : 'plan';
  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'plan') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createPosOpen, setCreatePosOpen] = useState(false);
  const [lineDialog, setLineDialog] = useState<{ open: boolean; line: PlanLineRow | null }>({
    open: false,
    line: null,
  });

  const holidayDates = useMemo(() => holidays.map((h) => h.holiday_date), [holidays]);
  const planLinesById = useMemo(() => new Map(planLines.map((l) => [l.id, l])), [planLines]);

  const openAdd = () => setLineDialog({ open: true, line: null });
  const openEdit = (line: PlanLineRow) => setLineDialog({ open: true, line });
  const openEditById = (id: string) => {
    const line = planLinesById.get(id);
    if (line) openEdit(line);
  };

  const summary = model?.summary;
  const dueNow = summary ? summary.ordersLate + summary.ordersDueNow : 0;
  const hasTargets = (model?.schedules.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing"
        title="Production planning"
        actions={
          <>
            <Button variant="outline" onClick={() => setSettingsOpen(true)}>
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setCreatePosOpen(true)}
              disabled={!model || model.orders.length === 0}
              title={model && model.orders.length === 0 ? 'Nothing to order right now' : undefined}
            >
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">Create draft POs</span>
              {model && model.orders.length > 0 && (
                <Badge variant="muted">{model.orders.length}</Badge>
              )}
            </Button>
            {canEdit && (
              <Button onClick={openAdd}>
                <Plus className="h-4 w-4" />
                Add target
              </Button>
            )}
          </>
        }
      />

      {error && (
        <p className="rounded-md border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
          Couldn’t load the plan: {error.message}. If this mentions{' '}
          <code>production_plan_lines</code>, migration 150 hasn’t been applied yet.
        </p>
      )}

      {!isLoading && !settingsSaved && !error && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-info-soft/70 bg-info-soft/20 px-4 py-3 text-sm">
          <span>
            Using default planning settings: Mon–Fri, {settings.horizonDays}-day horizon,{' '}
            {settings.safetyBufferDays}-day order-early buffer. Confirm them once so the dates
            reflect how the floor actually runs.
          </span>
          <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
            Review settings
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label={`Units to build (${settings.horizonDays}d)`}
          value={summary ? fmtQty(summary.unitsPlanned) : '—'}
          hint={
            summary
              ? `${summary.finishedGoods} ${summary.finishedGoods === 1 ? 'product' : 'products'} · ${summary.purchasedParts} purchased parts`
              : undefined
          }
          icon={<Factory className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Next stock-out"
          value={summary?.nextRunout ? shortDate(summary.nextRunout.date) : summary ? 'None' : '—'}
          hint={
            summary?.nextRunout
              ? `${model?.itemsById.get(summary.nextRunout.itemId)?.item.sku ?? ''} · ${summary.partsShort} ${summary.partsShort === 1 ? 'part runs' : 'parts run'} short without new orders`
              : summary && hasTargets
                ? 'Every part is covered through the horizon'
                : undefined
          }
          icon={<PackageOpen className="h-4 w-4" />}
          tone={
            summary?.partsWithUnavoidableShortage
              ? 'negative'
              : summary?.nextRunout
                ? 'info'
                : 'neutral'
          }
          loading={isLoading}
        />
        <MetricCard
          label="Orders to place now"
          value={summary ? String(dueNow) : '—'}
          hint={
            summary
              ? summary.ordersLate > 0
                ? `${summary.ordersLate} already past the last safe day · ${summary.ordersDueSoon} due this week`
                : `${summary.ordersDueSoon} due this week · ${formatCurrency(model?.cash.plannedCents ?? 0)} suggested in total`
              : undefined
          }
          icon={<ShoppingCart className="h-4 w-4" />}
          tone={summary?.ordersLate ? 'negative' : dueNow > 0 ? 'info' : 'neutral'}
          loading={isLoading}
        />
        <MetricCard
          label="Missing information"
          value={summary ? String(summary.blockingGaps + summary.warningGaps) : '—'}
          hint={
            summary
              ? `${summary.blockingGaps} blocking · ${summary.warningGaps} to check`
              : undefined
          }
          icon={<AlertTriangle className="h-4 w-4" />}
          tone={summary?.blockingGaps ? 'negative' : summary?.warningGaps ? 'info' : 'neutral'}
          loading={isLoading}
        />
      </div>

      {isLoading || !model ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : !hasTargets && tab === 'plan' ? (
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              icon={Target}
              title="No production targets yet"
              detail="Tell the planner what to build — a batch by a date, a daily rate, or a monthly number from the sales channels — and it will work out what to order and when."
              action={
                canEdit ? (
                  <Button onClick={openAdd}>
                    <Plus className="h-4 w-4" />
                    Add a target
                  </Button>
                ) : undefined
              }
            />
          </CardContent>
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
            <TabsTrigger value="plan" className="gap-1.5">
              <Target className="h-3.5 w-3.5" />
              Plan
            </TabsTrigger>
            <TabsTrigger value="parts" className="gap-1.5">
              <PackageOpen className="h-3.5 w-3.5" />
              Parts
              {model.summary.partsShort > 0 && (
                <Badge variant="warning-soft" className="ml-1 px-1.5 py-0 text-[10px]">
                  {model.summary.partsShort}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              Procurement calendar
            </TabsTrigger>
            <TabsTrigger value="cash" className="gap-1.5">
              <ShoppingCart className="h-3.5 w-3.5" />
              Cash
              {model.cash.overBudgetMonths > 0 && (
                <Badge variant="warning-soft" className="ml-1 px-1.5 py-0 text-[10px]">
                  {model.cash.overBudgetMonths}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="missing" className="gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Missing info
              {model.gaps.length > 0 && (
                <Badge
                  variant={model.summary.blockingGaps > 0 ? 'warning' : 'muted'}
                  className="ml-1 px-1.5 py-0 text-[10px]"
                >
                  {model.gaps.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plan" className="mt-6">
            <PlanTab
              model={model}
              planLines={planLines}
              canEdit={canEdit}
              onAdd={openAdd}
              onEdit={openEdit}
            />
          </TabsContent>
          <TabsContent value="parts" className="mt-6">
            <PartsTab model={model} />
          </TabsContent>
          <TabsContent value="calendar" className="mt-6">
            <CalendarTab model={model} />
          </TabsContent>
          <TabsContent value="cash" className="mt-6">
            <CashTab model={model} onOpenSettings={() => setSettingsOpen(true)} />
          </TabsContent>
          <TabsContent value="missing" className="mt-6">
            <MissingInfoTab
              model={model}
              onEditPlanLine={openEditById}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          </TabsContent>
        </Tabs>
      )}

      <PlanLineDialog
        key={lineDialog.line?.id ?? 'new'}
        open={lineDialog.open}
        onOpenChange={(open) => setLineDialog((s) => ({ ...s, open }))}
        line={lineDialog.line}
        settings={settings}
        holidays={holidayDates}
      />
      <PlanningSettingsDialog
        key={settingsOpen ? 'open' : 'closed'}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        holidays={holidays}
        canEdit={canEdit}
      />
      {model && (
        <CreatePurchaseOrdersDialog
          open={createPosOpen}
          onOpenChange={setCreatePosOpen}
          model={model}
          canCreate={canCreatePos}
        />
      )}
    </div>
  );
}
